import { abrirSeletorDeData } from '../utils/abrirSeletorDeData';
import { useExemplos } from '../utils/exemplosPessoais';
import React, { useState, useMemo, useEffect } from 'react';
import { Transaction, TransactionType, PaymentType } from '../types/Transaction';
import { PaymentMethod } from '../types/PaymentMethod';
import { Category } from '../types/Category';
import { queryTransactions, QueryLabel, TransactionQueryResult } from '../services/api';
import AskField from './AskField';
import './TransactionList.css';
import { totaisParaCabecalho } from '../utils/totalFiltrado';
import { situacaoDaLinha, EstadoDeConfirmacao } from '../utils/confirmacao';
import Toast from './Toast';
import { brlOrMasked } from '../utils/format';
import { naMoeda, nomeDaMoeda } from '../utils/moeda';
import { filtrarPorPeriodo, periodoAtivo, periodoInvertido, PERIODO_VAZIO, Periodo, BaseDaData } from '../utils/periodoDeDatas';
import { valorExibido } from '../utils/monthView';
import { baixarCsv, csvDasTransacoes } from '../utils/exportarTransacoes';
import { paginasVisiveis } from '../utils/paginas';
import ValorDoMes, { ModoDoValorDoMes } from './ValorDoMes';
import { hojeISOLocal } from '../utils/dataDoCartao';

interface TransactionListProps {
  /** A busca falhou: a lista vazia não significa "não há". */
  falhaDeCarga?: boolean;
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  transactions: Transaction[];
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => Promise<void> | void;
  projectionMonthLabel?: string | null;
  onClearProjectionFilter?: () => void;
  paymentMethods?: PaymentMethod[];
  categories?: Category[];
  initialTypeFilter?: 'all' | TransactionType;
  initialCategoryFilter?: 'all' | string;
  /**
   * TODAS as transações, não só as do mês exibido.
   *
   * A pergunta escrita quase nunca respeita o recorte da tela: "quais os
   * parcelamentos que fiz em dezembro" precisa enxergar dezembro mesmo com
   * agosto selecionado. Perguntar e receber "nada encontrado" por causa de um
   * recorte que ela não escolheu seria a resposta errada com cara de certa.
   */
  allTransactions?: Transaction[];
  /**
   * Lembretes ligados a lançamentos, e o que já foi confirmado NESTE mês.
   *
   * Opcional: sem ela nenhum ícone aparece, e a lista fica como sempre foi.
   * O estado não é uma coluna — vem do check do lembrete, por mês.
   */
  confirmacao?: EstadoDeConfirmacao;
  /** O mês exibido, 'YYYY-MM'. É por mês que a confirmação vale. */
  mesExibido?: string;
  /**
   * Confirmar o mês (15/09): informar o valor de uma mensal, desfazer, ou dar a
   * fatura do cartão como paga. Sem estes, tocar no ícone só explica.
   */
  onInformarValorDoMes?: (t: Transaction, mes: string, valor: number) => Promise<void>;
  onDesfazerValorDoMes?: (t: Transaction, mes: string) => Promise<void>;
  onFaturaPaga?: (paymentMethodId: string, mes: string, paga: boolean) => Promise<void>;
  /**
   * Veio do toque em "a pagar" / "a receber" embaixo do saldo (15/09). `em` é
   * a hora do toque: tocar de novo no mesmo reaplica o filtro.
   */
  filtroAguardando?: { tipo: 'pagar' | 'receber'; em: number } | null;
  /** O toque no card de um método, em Métodos. `em` como no de cima. */
  filtroMetodo?: { id: string; em: number } | null;
}

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  onEdit,
  onDelete,
  projectionMonthLabel,
  onClearProjectionFilter,
  paymentMethods = [],
  categories = [],
  initialTypeFilter,
  initialCategoryFilter,
  allTransactions,
  confirmacao,
  mesExibido,
  onInformarValorDoMes,
  onDesfazerValorDoMes,
  onFaturaPaga,
  filtroAguardando,
  filtroMetodo,
  hideValues,
  falhaDeCarga,
}) => {
  const exemplosDaBusca = useExemplos().busca;
  const [typeFilter, setTypeFilter] = useState<'all' | TransactionType>(initialTypeFilter ?? 'all');

  useEffect(() => {
    if (initialTypeFilter !== undefined) {
      setTypeFilter(initialTypeFilter);
      setPage(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTypeFilter]);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<'all' | PaymentType>('all');
  const [methodFilter, setMethodFilter] = useState<'all' | string>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>(initialCategoryFilter ?? 'all');
  const [daysFilter, setDaysFilter] = useState<'all' | 30 | 60 | 90>('all');
  /**
   * Só o que ainda aguarda confirmação — gasto ("a pagar") ou recebimento ("a
   * receber"), pela mesma regra do ícone (15/09). É o filtro do toque na linha
   * embaixo do saldo: ela vê exatamente o que compõe aquele número.
   */
  const [aguardandoFilter, setAguardandoFilter] = useState<'all' | 'pagar' | 'receber'>('all');
  useEffect(() => {
    if (!filtroAguardando) return;
    setAguardandoFilter(filtroAguardando.tipo);
    setTypeFilter('all');
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroAguardando?.em]);
  useEffect(() => {
    if (!filtroMetodo) return;
    setMethodFilter(filtroMetodo.id);
    setTypeFilter('all');
    setAguardandoFilter('all');
    setFiltersOpen(true);
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroMetodo?.em]);
  /**
   * O período, pela data de pagamento ou de compra (13/09).
   *
   * Com ele ativo, a busca roda sobre TODAS as transações, não sobre o mês da
   * tela: uma compra de outubro paga em outubro não está na lista de novembro,
   * e o período de outubro olhado com novembro na tela a perderia em silêncio.
   * A regra inteira está em `utils/periodoDeDatas.ts`.
   */
  const [periodo, setPeriodo] = useState<Periodo>(PERIODO_VAZIO);
  const comPeriodo = periodoAtivo(periodo);

  // Reseta o filtro de dias quando um mês de projeção é selecionado
  useEffect(() => {
    if (projectionMonthLabel) setDaysFilter('all');
  }, [projectionMonthLabel]);

  useEffect(() => {
    if (initialCategoryFilter !== undefined) {
      setCategoryFilter(initialCategoryFilter);
      if (initialCategoryFilter !== 'all') setFiltersOpen(true);
      setPage(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryFilter]);
  const [sortBy, setSortBy] = useState<'createdAt' | 'date'>('createdAt');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  /**
   * O mesmo texto do hover, para quem não tem hover.
   *
   * No celular não existe passar o mouse, então `title` é invisível — a
   * explicação simplesmente não chegaria. Tocar no ícone mostra o aviso.
   */
  const [avisoConfirmacao, setAvisoConfirmacao] = useState<string | null>(null);
  /** A linha cuja janela de confirmar o mês está aberta (15/09). */
  const [confirmando, setConfirmando] = useState<{ t: Transaction; modo: ModoDoValorDoMes } | null>(null);
  const PAGE_SIZE = 12;

  /* ── Perguntar a lista ─────────────────────────────────────────────────
     O texto, o carregando e o erro moram em AskField agora. O que fica aqui é
     só a RESPOSTA, que é o que difere desta tela para as outras duas. */
  const [resposta, setResposta] = useState<{
    ids: Set<string>;
    labels: QueryLabel[];
    unresolved: string[];
    count: number;
    total: number;
    dateBasis: string;
  } | null>(null);

  const aplicar = (r: TransactionQueryResult) => {
    setResposta({
      ids: new Set(r.matchIds ?? []),
      labels: r.labels ?? [],
      unresolved: r.unresolved ?? [],
      count: r.count ?? 0,
      total: r.total ?? 0,
      dateBasis: String((r.query as any)?.dateBasis ?? 'affects'),
    });
    setPage(1);
  };

  const handleDeleteClick = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSort = (field: 'createdAt' | 'date') => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const DEBIT_PREFIX = '__debit__';
  const WITHDRAW_PREFIX = '__withdraw__';

  // Item 9b: o nome da fonte vem resolvido do backend (debitSourceName), não
  // mais extraído de um rótulo dentro de payment_method. Renomear a fonte
  // passa a refletir na hora.
  const paymentMethodName = (id: string | null | undefined, t?: Transaction): string => {
    if (t?.debitSourceName) return t.debitSourceName;
    // Outras moedas (13/10): a compra, a venda e o gasto não têm método; o que
    // os paga é a carteira da moeda, e a quantia na moeda vai junto.
    if (t?.moeda) return t.valorNaMoeda ? `${naMoeda(t.valorNaMoeda, t.moeda)} · ${nomeDaMoeda(t.moeda)}` : nomeDaMoeda(t.moeda);
    if (!id) return '—';
    if (id.startsWith(DEBIT_PREFIX)) return id.slice(DEBIT_PREFIX.length);
    if (id.startsWith(WITHDRAW_PREFIX)) return id.slice(WITHDRAW_PREFIX.length);
    const found = paymentMethods.find(m => m.id === id);
    if (found) return found.name;
    const fallback: Record<string, string> = {
      cash: 'Dinheiro',
      credit_card: 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      pix: 'PIX',
      bank_transfer: 'Transferência Bancária',
      other: 'Outro',
    };
    return fallback[id] ?? id;
  };

  const isDebitTransaction = (t: Transaction) =>
    t.debitDirection ? t.debitDirection === 'debit' : !!t.paymentMethod?.startsWith(DEBIT_PREFIX);
  const isWithdrawTransaction = (t: Transaction) =>
    t.debitDirection ? t.debitDirection === 'withdraw' : !!t.paymentMethod?.startsWith(WITHDRAW_PREFIX);

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  /**
   * A compra só aparece quando é DIFERENTE do pagamento (13/09).
   *
   * Em quase toda linha as duas são a mesma data, e mostrar as duas sempre
   * dobraria o ruído da coluna inteira para responder uma pergunta que só
   * existe nas compras de cartão. Nas linhas anteriores a 13/09 a coluna é
   * nula, então nada muda de aparência.
   */
  const compraDiferente = (t: Transaction): boolean =>
    !!t.purchaseDate && t.purchaseDate.slice(0, 10) !== t.date.slice(0, 10);

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const formatCurrency = (value: number) => brlOrMasked(value, !!hideValues);

  /**
   * ⏳ esperando, ✓ confirmado, nada quando não há lembrete ligado.
   *
   * `sem-vinculo` é o caso comum, e desenhar algo nele transformaria o ícone
   * em papel de parede — aí ele deixa de significar qualquer coisa.
   */
  /**
   * O que dá para fazer ao tocar no ícone desta linha (15/09).
   *
   * Cartão com fatura manda, inclusive numa mensal no cartão — quem paga é a
   * fatura. Mensal fora de cartão se confirma pelo valor do mês. O resto (avulsa
   * com lembrete) não tem janela: confirma-se no lembrete.
   */
  const modoDaConfirmacao = (t: Transaction, st: string): ModoDoValorDoMes | null => {
    if (!mesExibido || !confirmacao?.pagamentos || t.debitDirection) return null;
    const pm = t.paymentMethod;
    if (pm && confirmacao.pagamentos.metodosComFatura.has(pm) && onFaturaPaga) {
      return { tipo: 'fatura', cartao: paymentMethodName(pm), paga: st === 'confirmada' };
    }
    if (t.isMonthly && !t.hasInstallments && onInformarValorDoMes) {
      return {
        tipo: 'mensal',
        descricao: t.description,
        receita: t.type === TransactionType.INCOME,
        previsto: valorExibido(t),
        informado: t.monthValues?.[mesExibido] ?? null,
      };
    }
    return null;
  };

  const iconeDeConfirmacao = (t: Transaction) => {
    if (!confirmacao || !mesExibido) return null;
    const st = situacaoDaLinha(t, mesExibido, confirmacao);
    if (st === 'sem-vinculo') return null;
    const esperando = st === 'aguardando';
    const ehReceita = t.type === TransactionType.INCOME;

    /*
     * O texto muda com o tipo, porque o mesmo ⏳ significa coisas opostas:
     * numa despesa o dinheiro ainda ESTÁ na conta, numa receita ele ainda NÃO
     * chegou. Dizer só "aguardando" deixaria a pessoa deduzir, e deduzir sobre
     * dinheiro é onde este app já errou.
     */
    /*
     * Desde 15/09 a confirmação não é só pelo lembrete: mensal se confirma
     * informando o valor do mês, compra de cartão pela fatura paga. O texto
     * diz o caminho que existe para ESTA linha.
     */
    const modo = modoDaConfirmacao(t, st);
    const como = modo?.tipo === 'fatura'
      ? 'Quando a fatura do cartão for marcada como paga'
      : modo?.tipo === 'mensal' ? 'Quando você informar o valor do mês' : 'Quando você confirmar pelo lembrete';
    const texto = esperando
      ? (ehReceita
          ? `Recebimento ainda não efetuado. ${como}, ele passa a contar como receita no saldo atual.`
          : `Pagamento ainda não efetuado. ${como}, ele passa a contar como débito no saldo atual.`)
      : (ehReceita
          ? 'Recebimento confirmado. Já conta como receita no saldo atual.'
          : 'Pagamento confirmado. Já conta como débito no saldo atual.');

    return (
      <button
        type="button"
        className={`tl-confirm ${ehReceita ? 'income' : 'expense'}${esperando ? '' : ' tl-confirm-ok'}`}
        title={texto}
        aria-label={texto}
        onClick={e => {
          // A linha inteira abre a edição. Tocar no ícone é outra intenção,
          // então o clique para aqui — senão explicar o ícone abriria um
          // formulário que ela não pediu.
          e.stopPropagation();
          // Com o que confirmar e um mês que já chegou, abre a janela; senão explica.
          if (modo && mesExibido <= hojeISOLocal().slice(0, 7)) setConfirmando({ t, modo });
          else setAvisoConfirmacao(texto);
        }}
      >
        {esperando ? (
          /*
             Anel de progresso PARADO, não uma ampulheta.
             `currentColor` de propósito: cor fixa em SVG não é CSS e escapa da
             troca de tema — foi assim que um `fill="white"` sobreviveu à
             tokenização (armadilha 9).
          */
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2.6" opacity="0.28" />
            <path
              d="M10 2a8 8 0 0 1 8 8"
              stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="9" fill="currentColor" />
            <path
              d="M6 10.4l2.6 2.6L14.2 7.4"
              stroke="var(--s-card)" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    );
  };

  // Métodos que aparecem nas transações exibidas (para popular o select)
  const usedMethods = useMemo(() => {
    // Item 9b: transferência de reserva tem paymentMethod null e não entra
    // no filtro de método — ela não tem método.
    // O método escolhido pelo card entra mesmo sem gasto no mês: sem ele o
    // select mostraria "Todos" com a lista vazia por baixo.
    const ids = Array.from(new Set([
      ...transactions.map(t => t.paymentMethod).filter((x): x is string => !!x),
      ...(methodFilter !== 'all' ? [methodFilter] : []),
    ]));
    return ids.map(id => ({ id, name: paymentMethodName(id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, paymentMethods, methodFilter]);

  const visibleTransactions = useMemo(() => {
    // Com uma pergunta respondida, ela manda: os seletores ficam de fora e a
    // busca roda sobre TUDO, não sobre o mês exibido. Cruzar os dois daria uma
    // lista que não responde nem uma coisa nem outra.
    if (resposta) {
      const base = allTransactions ?? transactions;
      return base
        .filter(t => resposta.ids.has(t.id))
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    }

    const cutoff = daysFilter !== 'all'
      ? new Date(Date.now() - daysFilter * 24 * 60 * 60 * 1000)
      : null;

    const base = comPeriodo ? filtrarPorPeriodo(allTransactions ?? transactions, periodo) : transactions;

    const filtered = base.filter(t => {
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (paymentTypeFilter !== 'all' && t.paymentType !== paymentTypeFilter) return false;
      if (methodFilter !== 'all' && t.paymentMethod !== methodFilter) return false;
      if (categoryFilter !== 'all') {
        if (categoryFilter === '__none__' && t.categoryId) return false;
        if (categoryFilter !== '__none__' && t.categoryId !== categoryFilter) return false;
      }
      if (cutoff && new Date(t.createdAt) < cutoff) return false;
      if (aguardandoFilter !== 'all') {
        const tipo = aguardandoFilter === 'pagar' ? TransactionType.EXPENSE : TransactionType.INCOME;
        if (t.type !== tipo || !confirmacao || !mesExibido) return false;
        if (situacaoDaLinha(t, mesExibido, confirmacao) !== 'aguardando') return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      // Com o período desdobrando parcelas, várias linhas têm o mesmo `date`
      // gravado; ordena pelo mês da ocorrência para "2/6, 3/6, 4/6" sair em ordem.
      const pagamento = (t: Transaction) =>
        t.displayOcorrencia ? `${t.displayOcorrencia}-${t.date.slice(8, 10)}` : t.date;
      const valA = sortBy === 'date' ? pagamento(a) : a.createdAt;
      const valB = sortBy === 'date' ? pagamento(b) : b.createdAt;
      const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [transactions, allTransactions, resposta, typeFilter, paymentTypeFilter, methodFilter, categoryFilter, daysFilter, aguardandoFilter, confirmacao, mesExibido, periodo, comPeriodo, sortBy, sortDir]);

  /*
   * A página volta para a 1 só quando ELA muda o que está vendo (15/09).
   *
   * O `setPage(1)` morava dentro do `useMemo` acima, que depende de
   * `transactions` — e essa lista chega com identidade nova a cada render do
   * App (`transactionsInMonth` monta um array novo), e de novo toda vez que a
   * aba volta e os dados são revalidados. O relato: "se eu estou analisando as
   * transações a página muda pra página 1 em vez de ficar na página que estou
   * vendo". Dado chegando de novo não é ela mudando de ideia.
   *
   * Aqui ficam os filtros, a ordem, a pergunta e o mês. `transactions` fica de
   * fora de propósito.
   */
  useEffect(() => {
    setPage(1);
  }, [resposta, typeFilter, paymentTypeFilter, methodFilter, categoryFilter, daysFilter, aguardandoFilter, periodo, sortBy, sortDir, mesExibido]);

  const activeFilterCount = [
    typeFilter !== 'all',
    paymentTypeFilter !== 'all',
    methodFilter !== 'all',
    categoryFilter !== 'all',
    daysFilter !== 'all',
    aguardandoFilter !== 'all',
    comPeriodo,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setTypeFilter('all');
    setPaymentTypeFilter('all');
    setMethodFilter('all');
    setCategoryFilter('all');
    setDaysFilter('all');
    setAguardandoFilter('all');
    setPeriodo(PERIODO_VAZIO);
    setPage(1);
  };

  /*
   * Há o que filtrar? O mês da tela pode estar vazio e o período ainda achar
   * coisa em outros meses — esconder os Filtros quando o MÊS está vazio
   * trancaria o filtro que existe justamente para olhar fora dele.
   */
  const temTransacoes = transactions.length > 0 || (allTransactions?.length ?? 0) > 0;

  /**
   * O total do que está na tela AGORA — depois dos filtros, e antes da
   * paginação. Somar só a página daria um número que muda quando ela vira de
   * página, que é a definição de número em que não se confia.
   *
   * Com uma pergunta ativa ele se cala: a caixa da resposta já mostra o
   * total, e o número de lá vem do backend com outra regra de soma. A razão
   * inteira está em `utils/totalFiltrado.ts`, puro e testado. Aqui só se
   * imprime.
   */
  const totais = useMemo(
    () => totaisParaCabecalho(visibleTransactions, !!resposta),
    [visibleTransactions, resposta],
  );

  /**
   * Baixa o que está na lista AGORA: todas as páginas, com os filtros, a
   * ordem e os valores que a tabela mostra. Sai mesmo com os valores
   * escondidos pelo olhinho — exportar é um pedido explícito dela.
   */
  const exportar = () => {
    baixarCsv(
      csvDasTransacoes(visibleTransactions, {
        categoria: t => categories.find(c => c.id === t.categoryId)?.name ?? '',
        metodo: t => { const nome = paymentMethodName(t.paymentMethod, t); return nome === '—' ? '' : nome; },
        valor: valorExibido,
      }),
      `fintracking-transacoes-${hojeISOLocal()}.csv`,
    );
  };

  const totalPages = Math.ceil(visibleTransactions.length / PAGE_SIZE);
  // A lista pode encolher com a página parada (apagou a última linha da última
  // página): mostra a última que existe em vez de uma página vazia.
  const paginaAtual = Math.min(page, Math.max(1, totalPages));
  const pagedTransactions = visibleTransactions.slice((paginaAtual - 1) * PAGE_SIZE, paginaAtual * PAGE_SIZE);

  /*
   * "Nenhuma transação" é uma AFIRMAÇÃO, e ela só pode ser feita quando a
   * lista chegou. Com o servidor fora do ar a lista vem vazia igual, e a tela
   * dizia "Nenhuma transação encontrada para Agosto de 2026" — afirmando não
   * haver o que ela nem conseguiu perguntar. Num app de dinheiro isso não lê
   * como falha, lê como perda.
   */
  const emptyMessage = falhaDeCarga
    ? 'Não consegui carregar suas transações. Isso não quer dizer que não há nenhuma, e sim que o servidor não respondeu.'
    : projectionMonthLabel
      ? `Nenhuma transação encontrada para ${projectionMonthLabel}.`
      : 'Nenhuma transação cadastrada ainda.';

  return (
    <div className="transaction-list-container">
      <Toast message={avisoConfirmacao} onDismiss={() => setAvisoConfirmacao(null)} />
      {confirmando && mesExibido && (
        <ValorDoMes
          modo={confirmando.modo}
          mes={mesExibido}
          fmt={formatCurrency}
          onFechar={() => setConfirmando(null)}
          onInformar={valor => onInformarValorDoMes!(confirmando.t, mesExibido, valor)}
          onDesfazer={() => onDesfazerValorDoMes!(confirmando.t, mesExibido)}
          onFatura={paga => onFaturaPaga!(confirmando.t.paymentMethod!, mesExibido, paga)}
        />
      )}
      <div className="transaction-list-header">
        <div className="tl-title-group">
          <h2>Transações</h2>
          {visibleTransactions.length > 0 && (
            <span className="tl-count-badge">{visibleTransactions.length}</span>
          )}
          {totais.length > 0 && (
            <span className="tl-total-group">
              {totais.map(parte => (
                <span
                  key={parte.chave}
                  className={`tl-total tl-total-${parte.chave}`}
                  title={parte.titulo}
                >
                  {parte.sinal ?? ''}{formatCurrency(parte.valor)}
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="tl-header-right">
          {projectionMonthLabel && !resposta && !comPeriodo && (
            <div className="projection-filter-badge">
              <span>{projectionMonthLabel}</span>
              <button
                className="btn-clear-filter"
                onClick={onClearProjectionFilter}
                title="Limpar filtro"
              >✕</button>
            </div>
          )}
          {visibleTransactions.length > 0 && (
            <button
              type="button"
              className="tl-filter-toggle tl-exportar"
              onClick={exportar}
              title={`Baixar as ${visibleTransactions.length} transações desta lista como planilha`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M7 1.5v7M4 6l3 3 3-3M2 11.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Exportar
            </button>
          )}
          {temTransacoes && !resposta && (
            <>
              <div className="tl-sort-group">
                <button
                  className={`tl-sort-btn${sortBy === 'createdAt' ? ' active' : ''}`}
                  onClick={() => toggleSort('createdAt')}
                  title="Ordenar por data de lançamento"
                >
                  Lançamento
                  <span className="tl-sort-arrow">{sortBy === 'createdAt' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </button>
                <button
                  className={`tl-sort-btn${sortBy === 'date' ? ' active' : ''}`}
                  onClick={() => toggleSort('date')}
                  title="Ordenar por data do pagamento"
                >
                  Pagamento
                  <span className="tl-sort-arrow">{sortBy === 'date' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </button>
              </div>
              <button
                className={`tl-filter-toggle${filtersOpen ? ' active' : ''}${activeFilterCount > 0 ? ' has-filters' : ''}`}
                onClick={() => setFiltersOpen(v => !v)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 3h12M3 7h8M5 11h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                Filtros
                {activeFilterCount > 0 && (
                  <span className="tl-filter-count">{activeFilterCount}</span>
                )}
              </button>
            </>
          )}
        </div>
      </div>


      {/* Perguntar a lista em vez de mexer em cinco seletores. A casca vem de
          AskField; o que sobra aqui é a resposta, que é o que difere. */}
      {(transactions.length > 0 || !!allTransactions?.length) && (
        <div className="tl-ask">
          <AskField<TransactionQueryResult>
            placeholder="eu gastei algo em pet esse mês?"
            ariaLabel="Perguntar sobre seus lançamentos"
            exemplos={exemplosDaBusca}
            onAsk={async texto => {
              const r = await queryTransactions(texto);
              if (!r.query) throw new Error(r.problem ?? 'Não entendi. Tente citar o que e quando.');
              aplicar(r);
              return r;
            }}
          >
            {(_r, limpar) => (
              <div className="tl-ask-resposta">
                <div className="tl-ask-linha">
                  <strong className={resposta?.count === 0 ? 'tl-ask-zero' : undefined}>
                    {resposta?.count === 0
                      ? 'Nada encontrado'
                      : `${resposta?.count} ${resposta?.count === 1 ? 'lançamento' : 'lançamentos'} · ${formatCurrency(resposta?.total ?? 0)}`}
                  </strong>
                  <button className="tl-ask-limpar" onClick={() => { setResposta(null); limpar(); }}>
                    limpar
                  </button>
                </div>

                {/* Os chips dizem o que foi entendido. Sem isso, um filtro errado
                    devolve uma lista plausível e nada denuncia o engano. */}
                <div className="tl-ask-chips">
                  {resposta?.labels.map((l, i) => (
                    <span key={`${l.field}-${i}`} className="tl-ask-chip">{l.label}</span>
                  ))}
                  {resposta?.dateBasis === 'purchase' && (
                    <span className="tl-ask-chip tl-ask-chip-nota" title="Filtrado pela data da compra, não pelos meses em que as parcelas caem">
                      pela data da compra
                    </span>
                  )}
                </div>

                {!!resposta?.unresolved.length && (
                  <div className="tl-ask-aviso">
                    Não encontrei {resposta.unresolved.map(u => `“${u}”`).join(', ')} no seu cadastro, então
                    esse pedaço da pergunta ficou de fora.
                  </div>
                )}

                <div className="tl-ask-escopo">
                  Busca em todos os lançamentos: o mês selecionado e os filtros ficam
                  suspensos enquanto a pergunta estiver ativa.
                </div>
              </div>
            )}
          </AskField>
        </div>
      )}

      {temTransacoes && !resposta && (
        <div className={`tl-filters${filtersOpen ? ' tl-filters-open' : ''}`}>
          <div className="tl-filter-group">
            {(['all', TransactionType.INCOME, TransactionType.EXPENSE] as const).map(v => (
              <button
                key={v}
                className={`tl-pill ${typeFilter === v ? 'active income-active' : ''} ${v === TransactionType.INCOME ? 'income-pill' : ''} ${v === TransactionType.EXPENSE ? 'expense-pill' : ''}`}
                onClick={() => setTypeFilter(v)}
              >
                {v === 'all' ? 'Todos' : v === TransactionType.INCOME ? 'Recebimento' : 'Gasto'}
              </button>
            ))}
          </div>

          <div className="tl-filter-group">
            {(['all', PaymentType.FIXED, PaymentType.VARIABLE] as const).map(v => (
              <button
                key={v}
                className={`tl-pill ${paymentTypeFilter === v ? 'active' : ''}`}
                onClick={() => setPaymentTypeFilter(v)}
              >
                {v === 'all' ? 'Fixo e Variável' : v === PaymentType.FIXED ? 'Fixo' : 'Variável'}
              </button>
            ))}
          </div>

          {!projectionMonthLabel && (
            <div className="tl-filter-group tl-filter-group-days">
              {(['all', 30, 60, 90] as const).map(v => (
                <button
                  key={v}
                  className={`tl-pill ${daysFilter === v ? 'active' : ''}`}
                  onClick={() => setDaysFilter(v)}
                >
                  {v === 'all' ? 'Todos os dias' : `Últimos ${v} dias`}
                </button>
              ))}
            </div>
          )}

          {usedMethods.length > 1 && (
            <select
              value={methodFilter}
              onChange={e => setMethodFilter(e.target.value)}
              className={`tl-method-select ${methodFilter !== 'all' ? 'active' : ''}`}
            >
              <option value="all">Método: Todos</option>
              {usedMethods.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}

          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className={`tl-method-select ${categoryFilter !== 'all' ? 'active' : ''}`}
            >
              <option value="all">Categoria: Todas</option>
              <option value="__none__">Sem categoria</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          <div className="tl-periodo">
            <span className="tl-periodo-rotulo">Período pela data de</span>
            <div className="tl-filter-group">
              {(['pagamento', 'compra'] as BaseDaData[]).map(b => (
                <button
                  key={b}
                  className={`tl-pill ${periodo.base === b ? 'active' : ''}`}
                  onClick={() => setPeriodo(p => ({ ...p, base: b }))}
                  title={b === 'pagamento'
                    ? 'Quando o dinheiro sai. Parcelas e mensalidades aparecem uma vez por pagamento no período.'
                    : 'Quando você comprou. Uma parcelada aparece uma vez, no dia da compra.'}
                >
                  {b === 'pagamento' ? 'Pagamento' : 'Compra'}
                </button>
              ))}
            </div>
            <label className="tl-periodo-data">
              De
              <input
                type="date"
                onClick={abrirSeletorDeData}
                value={periodo.de}
                onChange={e => setPeriodo(p => ({ ...p, de: e.target.value }))}
              />
            </label>
            <label className="tl-periodo-data">
              Até
              <input
                type="date"
                onClick={abrirSeletorDeData}
                value={periodo.ate}
                min={periodo.de || undefined}
                onChange={e => setPeriodo(p => ({ ...p, ate: e.target.value }))}
              />
            </label>
            {comPeriodo && (
              <p className={`tl-periodo-nota${periodoInvertido(periodo) ? ' tl-periodo-erro' : ''}`}>
                {periodoInvertido(periodo)
                  ? '"De" está depois de "Até". Troque uma das datas.'
                  : 'O período busca em todos os lançamentos: o mês selecionado fica de fora enquanto ele estiver preenchido.'}
              </p>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button className="tl-btn-clear-filters" onClick={clearFilters}>
              ✕ Limpar
            </button>
          )}
        </div>
      )}

      {/* O filtro que veio do saldo aparece FORA do painel de filtros: ela tocou
          lá em cima, e precisa ver aqui por que a lista encolheu. */}
      {aguardandoFilter !== 'all' && !resposta && (
        <div className="tl-filtro-aguardando">
          <span>
            Só o que falta confirmar: {aguardandoFilter === 'pagar' ? 'a pagar' : 'a receber'}
          </span>
          <button type="button" onClick={() => setAguardandoFilter('all')} aria-label="Tirar o filtro de aguardando">✕</button>
        </div>
      )}

      {visibleTransactions.length === 0 ? (
        activeFilterCount > 0 ? (
          <div className="empty-state">
            <p>Nenhuma transação para os filtros selecionados.</p>
          </div>
        ) : projectionMonthLabel ? (
          <div className="empty-state">
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div className="empty-preview-wrap">
            <span className="empty-preview-badge">Exemplo</span>
            {/* Desktop ghost table */}
            <div className="tl-table-wrapper tl-desktop-only empty-preview-ghost">
              <table className="tl-table">
                <thead>
                  <tr>
                    <th>Descrição</th><th>Valor</th><th>Categoria</th>
                    <th className="tl-col-center">Natureza</th><th>Método</th><th className="tl-col-center">Data</th><th className="tl-col-center">Detalhe</th><th className="tl-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="row-income">
                    <td className="tl-col-desc"><span className="tl-type-dot income" />Salário</td>
                    <td className="tl-col-value income">+R$&nbsp;4.500,00</td>
                    <td className="tl-col-category"><span className="tl-cat-badge" style={{ background: '#d1fae522', color: '#059669', borderColor: '#05966955' }}>Renda</span></td>
                    <td className="tl-col-center"><span className="tl-nature-badge fixed">Fixo</span></td>
                    <td className="tl-col-method">PIX</td>
                    <td className="tl-col-center tl-col-date">05/02/2025</td>
                    <td className="tl-col-center"><span className="detail-badge monthly">Mensal</span></td>
                    <td className="tl-col-actions"><button className="btn-delete">✕</button></td>
                  </tr>
                  <tr className="row-expense">
                    <td className="tl-col-desc"><span className="tl-type-dot expense" />Supermercado</td>
                    <td className="tl-col-value expense">-R$&nbsp;380,00</td>
                    <td className="tl-col-category"><span className="tl-cat-badge" style={{ background: '#fef3c722', color: '#d97706', borderColor: '#d9770655' }}>Alimentação</span></td>
                    <td className="tl-col-center"><span className="tl-nature-badge variable">Variável</span></td>
                    <td className="tl-col-method">Cartão de Débito</td>
                    <td className="tl-col-center tl-col-date">12/02/2025</td>
                    <td className="tl-col-center" />
                    <td className="tl-col-actions"><button className="btn-delete">✕</button></td>
                  </tr>
                  <tr className="row-expense">
                    <td className="tl-col-desc"><span className="tl-type-dot expense" />Netflix</td>
                    <td className="tl-col-value expense">-R$&nbsp;55,90</td>
                    <td className="tl-col-category"><span className="tl-cat-badge" style={{ background: '#fee2e222', color: '#ef4444', borderColor: '#ef444455' }}>Lazer</span></td>
                    <td className="tl-col-center"><span className="tl-nature-badge fixed">Fixo</span></td>
                    <td className="tl-col-method">Cartão de Crédito</td>
                    <td className="tl-col-center tl-col-date">18/02/2025</td>
                    <td className="tl-col-center"><span className="detail-badge installments">1/12x</span></td>
                    <td className="tl-col-actions"><button className="btn-delete">✕</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Mobile ghost cards */}
            <div className="tl-mobile-list tl-mobile-only empty-preview-ghost">
              {[
                { desc: 'Salário', value: '+R$ 4.500,00', income: true, cat: 'Renda', catColor: '#059669', method: 'PIX', date: '05/02/2025', badge: <span className="detail-badge monthly">Mensal</span> },
                { desc: 'Supermercado', value: '-R$ 380,00', income: false, cat: 'Alimentação', catColor: '#d97706', method: 'Cartão de Débito', date: '12/02/2025', badge: null },
                { desc: 'Netflix', value: '-R$ 55,90', income: false, cat: 'Lazer', catColor: '#ef4444', method: 'Crédito', date: '18/02/2025', badge: <span className="detail-badge installments">1/12x</span> },
              ].map((t, i) => (
                <div key={i} className={`tl-card ${t.income ? 'tl-card-income' : 'tl-card-expense'}`}>
                  <div className="tl-card-top">
                    <div className="tl-card-desc-wrap">
                      <span className={`tl-type-dot ${t.income ? 'income' : 'expense'}`} />
                      <span className="tl-card-desc">{t.desc}</span>
                    </div>
                    <span className={`tl-card-value ${t.income ? 'income' : 'expense'}`}>{t.value}</span>
                  </div>
                  <div className="tl-card-bottom">
                    <div className="tl-card-tags">
                      <span className="tl-cat-badge" style={{ background: t.catColor + '22', color: t.catColor, borderColor: t.catColor + '55' }}>{t.cat}</span>
                      <span className={`tl-nature-badge ${t.income ? 'fixed' : 'variable'}`}>{t.income ? 'Fixo' : 'Variável'}</span>
                      {t.badge}
                    </div>
                    <div className="tl-card-meta">
                      <div className="tl-card-quando">
                        <span className="tl-card-method">{t.method}</span>
                        <span className="tl-card-date">{t.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="empty-cta">
              <p className="empty-cta-title">Nenhuma transação ainda</p>
              <p className="empty-cta-sub">Use o formulário acima para registrar sua primeira transação.</p>
            </div>
          </div>
        )
      ) : (
        <>
          {/* ── Desktop: tabela ── */}
          <div className="tl-table-wrapper tl-desktop-only">
            <table className="tl-table">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Categoria</th>
                  <th className="tl-col-center">Natureza</th>
                  <th>Método</th>
                  <th className="tl-col-center">Data</th>
                  <th className="tl-col-center">Detalhe</th>
                  <th className="tl-col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {pagedTransactions.map((transaction) => (
                  <tr
                    key={`${transaction.id}-${transaction.displayInstallment ?? transaction.currentInstallment}-${transaction.displayOcorrencia ?? ''}`}
                    className={transaction.type === TransactionType.INCOME ? 'row-income' : 'row-expense'}
                    onClick={() => onEdit(transaction)}
                  >
                    <td className="tl-col-desc">
                      {/* O ícone SUBSTITUI o ponto: os dois dizem despesa ou
                          receita pela cor, e mostrar os dois seria repetir a
                          mesma informação ocupando o dobro do espaço. */}
                      {iconeDeConfirmacao(transaction) ?? (
                        <span className={`tl-type-dot ${transaction.type === TransactionType.INCOME ? 'income' : 'expense'}`} />
                      )}
                      {transaction.description}
                    </td>
                    <td className={`tl-col-value ${transaction.type === TransactionType.INCOME ? 'income' : 'expense'}`}>
                      {transaction.type === TransactionType.INCOME ? '+' : '-'}
                      {formatCurrency(valorExibido(transaction))}
                    </td>
                    <td className="tl-col-category">
                      {(() => {
                        const cat = categories.find(c => c.id === transaction.categoryId);
                        return cat
                          ? <span className="tl-cat-badge" style={{ background: cat.color + '22', color: cat.color, borderColor: cat.color + '55' }}>{cat.name}</span>
                          : null;
                      })()}
                    </td>
                    <td className="tl-col-center">
                      <span className={`tl-nature-badge ${transaction.paymentType === PaymentType.FIXED ? 'fixed' : 'variable'}`}>
                        {transaction.paymentType === PaymentType.FIXED ? 'Fixo' : 'Variável'}
                      </span>
                    </td>
                    <td className="tl-col-method">
                      {isDebitTransaction(transaction) && (
                        <span className="tl-debit-badge" title="Debitado de investimento ou reserva">↙</span>
                      )}
                      {isWithdrawTransaction(transaction) && (
                        <span className="tl-debit-badge tl-withdraw-badge" title="Retirado de investimento ou reserva">↗</span>
                      )}
                      {paymentMethodName(transaction.paymentMethod, transaction)}
                    </td>
                    <td className="tl-col-center tl-col-date">
                      {formatDate(transaction.date)}
                      {compraDiferente(transaction) && (
                        <span className="tl-compra" title="Data em que você comprou. O mês do gasto segue a data do pagamento.">
                          compra {formatDate(transaction.purchaseDate!)}
                        </span>
                      )}
                    </td>
                    <td className="tl-col-center">
                      {transaction.isMonthly && (
                        <span className="detail-badge monthly">Mensal</span>
                      )}
                      {transaction.hasInstallments && transaction.installments && (
                        <span className="detail-badge installments">
                          {transaction.displayInstallment ?? transaction.currentInstallment ?? 1}/{transaction.installments}x
                        </span>
                      )}
                    </td>
                    <td className="tl-col-actions" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleDeleteClick(transaction.id)}
                        className="btn-delete"
                        title="Excluir"
                        disabled={deletingId === transaction.id}
                      >
                        {deletingId === transaction.id
                          ? <span className="tl-delete-spinner" />
                          : '✕'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Mobile: cards ── */}
          <div className="tl-mobile-list tl-mobile-only">
            {pagedTransactions.map((transaction) => {
              const isIncome = transaction.type === TransactionType.INCOME;
              const cat = categories.find(c => c.id === transaction.categoryId);
              return (
                <div
                  key={`${transaction.id}-${transaction.displayInstallment ?? transaction.currentInstallment}-${transaction.displayOcorrencia ?? ''}`}
                  className={`tl-card ${isIncome ? 'tl-card-income' : 'tl-card-expense'}`}
                  onClick={() => onEdit(transaction)}
                >
                  <div className="tl-card-top">
                    <div className="tl-card-desc-wrap">
                      {iconeDeConfirmacao(transaction) ?? (
                        <span className={`tl-type-dot ${isIncome ? 'income' : 'expense'}`} />
                      )}
                      <span className="tl-card-desc">{transaction.description}</span>
                    </div>
                    <span className={`tl-card-value ${isIncome ? 'income' : 'expense'}`}>
                      {isIncome ? '+' : '-'}{formatCurrency(valorExibido(transaction))}
                    </span>
                  </div>
                  <div className="tl-card-bottom">
                    <div className="tl-card-tags">
                      {cat && (
                        <span className="tl-cat-badge" style={{ background: cat.color + '22', color: cat.color, borderColor: cat.color + '55' }}>
                          {cat.name}
                        </span>
                      )}
                      <span className={`tl-nature-badge ${transaction.paymentType === PaymentType.FIXED ? 'fixed' : 'variable'}`}>
                        {transaction.paymentType === PaymentType.FIXED ? 'Fixo' : 'Variável'}
                      </span>
                      {transaction.isMonthly && (
                        <span className="detail-badge monthly">Mensal</span>
                      )}
                      {transaction.hasInstallments && transaction.installments && (
                        <span className="detail-badge installments">
                          {transaction.displayInstallment ?? transaction.currentInstallment ?? 1}/{transaction.installments}x
                        </span>
                      )}
                    </div>
                    <div className="tl-card-meta">
                      {/* Método e datas empilhados, numa coluna só — ver a nota
                          em `.tl-card-quando`. */}
                      <div className="tl-card-quando">
                        <span className="tl-card-method">
                          {isDebitTransaction(transaction) && (
                            <span className="tl-debit-badge" title="Debitado de investimento ou reserva">↙</span>
                          )}
                          {isWithdrawTransaction(transaction) && (
                            <span className="tl-debit-badge tl-withdraw-badge" title="Retirado de investimento ou reserva">↗</span>
                          )}
                          {paymentMethodName(transaction.paymentMethod, transaction)}
                        </span>
                        <span className="tl-card-date">
                          {formatDate(transaction.date)}
                          {compraDiferente(transaction) && (
                            <span className="tl-compra">compra {formatDate(transaction.purchaseDate!)}</span>
                          )}
                        </span>
                      </div>
                      <button
                        className="btn-delete"
                        onClick={e => { e.stopPropagation(); handleDeleteClick(transaction.id); }}
                        title="Excluir"
                        disabled={deletingId === transaction.id}
                      >
                        {deletingId === transaction.id
                          ? <span className="tl-delete-spinner" />
                          : '✕'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Paginação (compartilhada) */}
          {totalPages > 1 && (
            <div className="tl-pagination">
              <span className="tl-page-info">
                {(paginaAtual - 1) * PAGE_SIZE + 1}–{Math.min(paginaAtual * PAGE_SIZE, visibleTransactions.length)} de {visibleTransactions.length}
              </span>
              <div className="tl-page-controls">
                <button className="tl-page-btn" onClick={() => setPage(Math.max(1, paginaAtual - 1))} disabled={paginaAtual === 1}>‹</button>
                {/* Primeira, última e a vizinhança da atual: um botão por página
                    não cabia no celular e fazia a página inteira rolar de lado. */}
                {paginasVisiveis(paginaAtual, totalPages).map((p, i) => (
                  p === '…'
                    ? <span key={`salto-${i}`} className="tl-page-gap" aria-hidden="true">…</span>
                    : <button key={p} className={`tl-page-btn ${p === paginaAtual ? 'active' : ''}`} onClick={() => setPage(p)} aria-current={p === paginaAtual ? 'page' : undefined}>{p}</button>
                ))}
                <button className="tl-page-btn" onClick={() => setPage(Math.min(totalPages, paginaAtual + 1))} disabled={paginaAtual === totalPages}>›</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionList;
